import { FlexSpinner, onFormatDate } from 'components'
import {
  FormikSingleUserPicker,
  FormikSingleWorkerPicker,
} from 'components/PersonPicker'
import { FieldProps } from 'formik'
import { AuthorizedField as Field } from 'forms/AuthorizedField'
import { FieldStack, FormStack } from 'forms/FormStack'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { Participant, PersonType } from 'model'
import { Label, Stack, Text } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useDoubleBookingsParticipantsQuery } from 'views/ProvingDetailsView/hooks'

interface DoubleBookingInfo {
  participant: Participant
  provingGroundTitle: string
  provingDescription: string
  provingNr: string
}

export const ProvingDetailsParticipantReassignForm: React.FC<{
  participant: Participant
}> = ({ participant }) => {
  const { t } = useTranslation()
  const [selectedType] = React.useState<PersonType | undefined>(() => {
    if (participant.person) {
      return participant.person.type
    } else {
      if (participant.unknownPerson) {
        return participant.unknownPerson.personType
      }
      return undefined
    }
  })
  const formikContext = useCurrentFormik()
  React.useEffect(() => {
    formikContext.setFieldValue('personType', selectedType)
    // WARNING! Do NOT add formikContext to dependencies.
    // Page will get stuck!
  }, [selectedType])
  const { data, loading } = useDoubleBookingsParticipantsQuery()
  const provings = data && data.cachedProvings ? data.cachedProvings : []
  const [doubleBookings, setDoubleBookings] = React.useState<
    DoubleBookingInfo[]
  >([])
  // const options: IDropdownOption[] = [
  //   { key: 'worker', text: t('dropdown.option.worker') },
  //   { key: 'user', text: t('dropdown.option.pag') },
  // ]
  const checkDoubleBookings = React.useCallback(
    (personIds: string[]) => {
      const res: DoubleBookingInfo[] = []
      provings.forEach((prov) => {
        ;(prov.participants || []).forEach((p) => {
          if (
            p.person &&
            p.person._id &&
            personIds.includes(p.person._id) &&
            p._id !== participant._id
          ) {
            if (
              p.dateFrom <= participant.dateTo &&
              p.dateTo >= participant.dateFrom
            ) {
              res.push({
                participant: p,
                provingGroundTitle: prov.provingGround
                  ? prov.provingGround.title
                  : '',
                provingNr: prov.id,
                provingDescription: prov.title,
              })
            }
          }
        })
      })
      return res
    },
    [provings, participant]
  )

  return (
    <FlexSpinner loading={loading}>
      <FormStack>
        <Label>{t('Teilnehmer neu zuordnen')}</Label>
        {/* <FieldStackItem
        name="personType"
        render={fieldProps => (
          <FormikDropdown
            label={t('field.participantType.label')}
            options={options}
            onChanged={option => {
              setSelectedType(option.key as PersonType)
            }}
            {...fieldProps}
          />
        )}
      /> */}
        {selectedType === PersonType.User && (
          <FieldStack>
            <Stack.Item className="composite-field">
              <Label>{t('field.pag.label')}</Label>
              <Field
                name={'personId'}
                render={(fieldProps: FieldProps<any>) => (
                  <FormikSingleUserPicker
                    onChange={(items) => {
                      if (items) {
                        setDoubleBookings(
                          checkDoubleBookings(
                            items.map((i) => (i as any).userId)
                          )
                        )
                      }
                    }}
                    {...fieldProps}
                  />
                )}
              />
            </Stack.Item>
          </FieldStack>
        )}
        {selectedType === PersonType.Worker && (
          <FieldStack>
            <Stack.Item className="composite-field">
              <Label>{t('field.worker.label')}</Label>
              <Field
                name={'personId'}
                render={(fieldProps: FieldProps<any>) => (
                  <FormikSingleWorkerPicker
                    onChange={(items) => {
                      if (items) {
                        setDoubleBookings(
                          checkDoubleBookings(
                            items.map((i) => (i as any).userId)
                          )
                        )
                      }
                    }}
                    {...fieldProps}
                  />
                )}
              />
            </Stack.Item>
          </FieldStack>
        )}
        {selectedType === PersonType.External && (
          <FieldStack>
            <Stack.Item className="composite-field">
              <Label>{t('field.extern.label')}</Label>
              <Field
                name={'personId'}
                render={(fieldProps: FieldProps<any>) => (
                  <FormikSingleUserPicker
                    onChange={(items) => {
                      if (items) {
                        setDoubleBookings(
                          checkDoubleBookings(
                            items.map((i) => (i as any).userId)
                          )
                        )
                      }
                    }}
                    {...fieldProps}
                  />
                )}
              />
            </Stack.Item>
          </FieldStack>
        )}
        {doubleBookings.length > 0 && (
          <FieldStack>
            <Text variant="xLarge" as="h2">
              ACHTUNG!
            </Text>
            <Stack.Item>
              <Label>
                {t(
                  `Folgender Teilnehmer ist zum Zeitraum ${onFormatDate(
                    new Date(participant.dateFrom)
                  )}
                   bis ${onFormatDate(
                     new Date(participant.dateTo)
                   )} bereits gebucht`
                )}
              </Label>
              {doubleBookings.map((x: DoubleBookingInfo, i: number) => {
                return (
                  <FieldStack key={i}>
                    <Label>
                      {x.participant.person ? x.participant.person.name : ''}
                    </Label>
                    <table>
                      <tbody>
                        <tr>
                          <td>Standort: {x.provingGroundTitle}</td>
                        </tr>
                        <tr>
                          <td>Erprobungsnr.: {x.provingNr}</td>
                        </tr>
                        <tr>
                          <td>Erprobung: {x.provingDescription}</td>
                        </tr>
                        <tr>
                          <td>
                            Abteilung:{' '}
                            {x.participant.person
                              ? x.participant.person.department
                              : ''}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            Datum:{' '}
                            {onFormatDate(new Date(x.participant.dateFrom))} bis{' '}
                            {onFormatDate(new Date(x.participant.dateTo))}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <hr />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </FieldStack>
                )
              })}
              <Label>{t('Möchten Sie den Teilnehmer trotzdem buchen?')}</Label>
            </Stack.Item>
          </FieldStack>
        )}
      </FormStack>
    </FlexSpinner>
  )
}
